import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

export default function CustomItemModal({ open, onClose, onAdd }) {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    quantity: '1',
    taxable: true,
    description: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price) || price <= 0) {
      newErrors.price = 'Please enter a valid price greater than 0';
    }
    
    const qty = parseInt(formData.quantity);
    if (!formData.quantity || isNaN(qty) || qty < 1) {
      newErrors.quantity = 'Please enter a valid quantity (minimum 1)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validateForm()) {
      return;
    }

    // Generate unique SKU for custom item
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    const customSku = `CUSTOM-${timestamp}-${random}`;

    const customItem = {
      sku: customSku,
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      qty: parseInt(formData.quantity),
      taxable: formData.taxable,
      description: formData.description.trim(),
      image: null, // Custom items don't have images
      isCustomItem: true // Flag to identify custom items
    };

    onAdd(customItem);
    
    // Reset form
    setFormData({
      name: '',
      price: '',
      quantity: '1',
      taxable: true,
      description: ''
    });
    setErrors({});
    
    onClose();
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      name: '',
      price: '',
      quantity: '1',
      taxable: true,
      description: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl">Add Custom Item</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Product Name */}
          <div className="space-y-2">
            <Label htmlFor="custom-name" className="text-base font-medium">
              Product Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="custom-name"
              placeholder="e.g. Custom Service"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`h-12 text-base ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label htmlFor="custom-price" className="text-base font-medium">
              Price <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base text-gray-500">$</span>
              <Input
                id="custom-price"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                className={`h-12 text-base pl-7 ${errors.price ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.price && (
              <p className="text-sm text-red-500">{errors.price}</p>
            )}
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="custom-quantity" className="text-base font-medium">
              Quantity <span className="text-red-500">*</span>
            </Label>
            <Input
              id="custom-quantity"
              type="number"
              min="1"
              placeholder="1"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              className={`h-12 text-base ${errors.quantity ? 'border-red-500' : ''}`}
            />
            {errors.quantity && (
              <p className="text-sm text-red-500">{errors.quantity}</p>
            )}
          </div>

          {/* Tax Toggle */}
          <div className="flex items-center justify-between py-2">
            <Label htmlFor="custom-tax" className="text-base font-medium">
              Apply Tax
            </Label>
            <Switch
              id="custom-tax"
              checked={formData.taxable}
              onCheckedChange={(checked) => handleChange('taxable', checked)}
            />
          </div>

          {/* Description (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="custom-description" className="text-base font-medium">
              Description <span className="text-gray-400 font-normal">(Optional)</span>
            </Label>
            <Textarea
              id="custom-description"
              placeholder="Add any notes or details..."
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="min-h-[80px] text-base resize-none"
              maxLength={200}
            />
            <p className="text-xs text-gray-500 text-right">
              {formData.description.length}/200
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="h-12 px-6 text-base"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            className="h-12 px-6 text-base"
          >
            Add to Cart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
